import { Component, OnInit, ViewChild, AfterViewInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup } from '@angular/forms';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatPaginatorModule, MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatMenuModule } from '@angular/material/menu';
import { MatDialogModule } from '@angular/material/dialog';
import { MatCardModule } from '@angular/material/card';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatChipsModule } from '@angular/material/chips';
import { SupportService } from './supportservice.service';
import { DatePipe } from '@angular/common';

interface SupportTicket {
  _id: string;
  userId: string;
  email: string;
  comments: string;
  document: string | null;
  status: 'open' | 'closed' | 'in-progress';
  createdAt: string;
  updatedAt: string;
  __v?: number;
}

interface PaginationInfo {
  total: number;
  totalPages: number;
  currentPage: number;
  perPage: number;
}

interface SupportTicketsData {
  tickets: SupportTicket[];
  pagination: PaginationInfo;
}

interface SupportTicketsResponse {
  success: boolean;
  data: SupportTicketsData;
  message?: string;
}

@Component({
  selector: 'app-user-feedback',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatTableModule,
    MatPaginatorModule,
    MatFormFieldModule,
    MatSelectModule,
    MatInputModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatIconModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    MatMenuModule,
    MatDialogModule,
    MatCardModule,
    MatSnackBarModule,
    MatChipsModule
  ],
  templateUrl: './user-feedback.component.html',
  styleUrl: './user-feedback.component.scss',
  providers: [DatePipe]
})
export class UserFeedbackComponent implements OnInit, AfterViewInit {
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  // Form and Data
  filterForm: FormGroup;
  dataSource = new MatTableDataSource<SupportTicket>([]);
  displayedTickets: SupportTicket[] = [];
  
  // Loading states
  isLoading = false;
  isFilterLoading = false;
  hasSearched = false;
  
  // Pagination
  pageSize = 10;
  pageSizeOptions = [5, 10, 25, 50];
  currentPage = 0;
  totalItems = 0;
  
  // Expanded rows
  expandedTicketId: string | null = null;
  
  // Search
  searchQuery = '';
  showSearchDropdown = false;
  filteredTickets: SupportTicket[] = [];
  selectedTicket: SupportTicket | null = null;
  
  // Filters data
  userEmails: string[] = [];
  isEmailsLoading = false;
  
  // Status options
  statusOptions = [
    { value: '', label: 'All Status' },
    { value: 'open', label: 'Open' },
    { value: 'in-progress', label: 'In Progress' },
    { value: 'closed', label: 'Closed' }
  ];

  constructor(
    private fb: FormBuilder,
    private supportService: SupportService,
    private datePipe: DatePipe
  ) {
    this.filterForm = this.fb.group({
      email: [''],
      status: ['']
    });
  }

  ngOnInit(): void {
    this.loadTickets();
    this.loadUserEmails();
  }

  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator;
  }

  loadUserEmails(): void {
    this.isEmailsLoading = true;
    // In a real app, you'd call an API to get unique emails
    this.isEmailsLoading = false;
    this.userEmails = ['aziz@gmail.com', 'user2@example.com', 'user3@example.com'];
  }

  loadTickets(): void {
    this.isLoading = true;
    this.supportService.getUserTickets().subscribe({
      next: (response: SupportTicketsResponse) => {
        if (response.success && response.data) {
          this.dataSource.data = response.data.tickets || [];
          this.displayedTickets = [...this.dataSource.data];
          this.totalItems = response.data.pagination?.total || 0;
        } else {
          this.dataSource.data = [];
          this.displayedTickets = [];
          this.totalItems = 0;
        }
        this.hasSearched = true;
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error loading tickets:', error);
        this.isLoading = false;
        this.dataSource.data = [];
        this.displayedTickets = [];
        this.totalItems = 0;
      }
    });
  }

  onSearchInput(): void {
    if (!this.searchQuery.trim()) {
      this.filteredTickets = [];
      this.showSearchDropdown = false;
      return;
    }

    const query = this.searchQuery.toLowerCase().trim();
    this.filteredTickets = this.dataSource.data.filter(ticket =>
      ticket.email.toLowerCase().includes(query) ||
      (ticket.comments && ticket.comments.toLowerCase().includes(query)) ||
      ticket.status.toLowerCase().includes(query)
    );

    this.showSearchDropdown = this.filteredTickets.length > 0;
  }

  onSearchBlur(): void {
    setTimeout(() => {
      this.showSearchDropdown = false;
    }, 200);
  }

  clearSearch(): void {
    this.searchQuery = '';
    this.filteredTickets = [];
    this.showSearchDropdown = false;
    this.selectedTicket = null;
    this.displayedTickets = [...this.dataSource.data];
    this.totalItems = this.dataSource.data.length;
    this.currentPage = 0;
  }

  selectTicket(ticket: SupportTicket): void {
    this.selectedTicket = ticket;
    this.searchQuery = ticket.email;
    this.showSearchDropdown = false;
    
    // Filter to show only selected ticket
    this.displayedTickets = [ticket];
    this.totalItems = 1;
    this.currentPage = 0;
  }

  toggleRow(ticketId: string, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    
    if (this.expandedTicketId === ticketId) {
      this.expandedTicketId = null;
    } else {
      this.expandedTicketId = ticketId;
    }
  }

  isTicketExpanded(ticketId: string): boolean {
    return this.expandedTicketId === ticketId;
  }

  applyFilters(): void {
    this.isFilterLoading = true;
    
    const filters = this.filterForm.value;
    let filteredTickets = [...this.dataSource.data];
    
    if (filters.email) {
      filteredTickets = filteredTickets.filter(ticket => 
        ticket.email.toLowerCase().includes(filters.email.toLowerCase())
      );
    }
    
    if (filters.status) {
      filteredTickets = filteredTickets.filter(ticket => 
        ticket.status === filters.status
      );
    }
    
    this.displayedTickets = filteredTickets;
    this.totalItems = filteredTickets.length;
    this.currentPage = 0;
    this.isFilterLoading = false;
  }

  clearFilter(): void {
    this.filterForm.reset({
      email: '',
      status: ''
    });
    this.displayedTickets = [...this.dataSource.data];
    this.totalItems = this.dataSource.data.length;
    this.currentPage = 0;
  }

  updateTicketStatus(ticketId: string, newStatus: 'open' | 'closed' | 'in-progress'): void {
    this.supportService.updateTicketStatus(ticketId, { status: newStatus }).subscribe({
      next: (response) => {
        if (response.success) {
          // Update local data
          const ticketIndex = this.dataSource.data.findIndex(t => t._id === ticketId);
          if (ticketIndex !== -1) {
            this.dataSource.data[ticketIndex].status = newStatus;
            this.dataSource.data[ticketIndex].updatedAt = new Date().toISOString();
            this.dataSource.data = [...this.dataSource.data];
            
            // Update displayed tickets if needed
            const displayIndex = this.displayedTickets.findIndex(t => t._id === ticketId);
            if (displayIndex !== -1) {
              this.displayedTickets[displayIndex].status = newStatus;
              this.displayedTickets[displayIndex].updatedAt = new Date().toISOString();
              this.displayedTickets = [...this.displayedTickets];
            }
          }
        }
      },
      error: (error) => {
        console.error('Error updating ticket status:', error);
      }
    });
  }

  getStatusClass(status: string): string {
    switch (status) {
      case 'open':
        return 'bg-red-100 text-red-800';
      case 'in-progress':
        return 'bg-yellow-100 text-yellow-800';
      case 'closed':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  }

  getStatusIcon(status: string): string {
    switch (status) {
      case 'open':
        return 'error';
      case 'in-progress':
        return 'pending';
      case 'closed':
        return 'check_circle';
      default:
        return 'help';
    }
  }

  formatDate(date: string): string {
    return this.datePipe.transform(date, 'medium') || date;
  }

  formatDateShort(date: string): string {
    return this.datePipe.transform(date, 'MMM d, y') || date;
  }

  getTimeFromDate(date: string): string {
    return this.datePipe.transform(date, 'h:mm a') || '';
  }

  onPageChange(event: PageEvent): void {
    this.currentPage = event.pageIndex;
    this.pageSize = event.pageSize;
  }

  refreshTickets(): void {
    this.loadTickets();
    this.clearSearch();
    this.clearFilter();
    this.expandedTicketId = null;
  }

  openDocument(documentUrl: string | null): void {
    if (documentUrl) {
      window.open(documentUrl, '_blank');
    }
  }

  getOpenTicketCount(): number {
    return this.dataSource.data.filter(ticket => ticket.status === 'open').length;
  }

  getClosedTicketCount(): number {
    return this.dataSource.data.filter(ticket => ticket.status === 'closed').length;
  }

  getInProgressTicketCount(): number {
    return this.dataSource.data.filter(ticket => ticket.status === 'in-progress').length;
  }

  trackByFn(index: number, ticket: SupportTicket): string {
    return ticket._id;
  }

  get isSearchActive(): boolean {
    return this.searchQuery.trim() !== '' || this.selectedTicket !== null;
  }
}